import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Plus, Search } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import {
    createAdvertiser,
    getAdvertiserDashboard,
    listAdvertisers,
    type AdvertiserDashboardDto,
    type AdvertiserDto,
    type CreateAdvertiserRequest,
} from "@/api/advertisers";

type StatusFilter = "all" | "active" | "archived";

const STATUS_VARIANT: Record<string, "success" | "muted" | "warning" | "default"> = {
    active: "success",
    archived: "muted",
};

export function AdvertisersListPage(): React.ReactNode {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [createOpen, setCreateOpen] = React.useState(false);
    const [searchInput, setSearchInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 200);
        return () => clearTimeout(t);
    }, [searchInput]);
    const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");

    // includeArchived=true so the status filter can show archived rows without
    // a second roundtrip. The full advertiser set is expected to be small (tens
    // per tenant, not thousands) so we paginate client-side.
    const listQuery = useQuery({
        queryKey: ["advertisers", "list"],
        queryFn: () => listAdvertisers(true),
    });

    const rows: AdvertiserDto[] = React.useMemo(() => {
        const all = listQuery.data ?? [];
        return all.filter((a) => {
            if (statusFilter !== "all" && a.status !== statusFilter) return false;
            if (search) {
                const hay = `${a.slug} ${a.name} ${a.contactEmail ?? ""}`.toLowerCase();
                if (!hay.includes(search)) return false;
            }
            return true;
        });
    }, [listQuery.data, search, statusFilter]);

    // KPIs per advertiser — fetched lazily once the list resolves. Failures
    // degrade to "—" cells; we don't block the whole table on a single 500.
    const dashboardResults = useQueries({
        queries: rows.map((a) => ({
            queryKey: ["advertiser", a.id, "dashboard"],
            queryFn: () => getAdvertiserDashboard(a.id),
            staleTime: 30_000,
        })),
    });
    const dashboardById = React.useMemo(() => {
        const m = new Map<number, AdvertiserDashboardDto | undefined>();
        rows.forEach((a, i) => m.set(a.id, dashboardResults[i]?.data));
        return m;
    }, [rows, dashboardResults]);

    return (
        <>
            <PageHeader
                title="Advertisers"
                description="Billing-side counterparties: who paid for the traffic landing on your pages and drafts."
                actions={
                    <Button data-testid="new-advertiser-button" onClick={() => setCreateOpen(true)}>
                        <Plus className="h-4 w-4" />
                        New advertiser
                    </Button>
                }
            />
            <div className="space-y-3 px-6 py-4">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 min-w-[260px]">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by slug, name or contact email…"
                            className="h-7 border-0 bg-transparent px-0 focus-visible:ring-0"
                            data-testid="advertisers-search"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                        <SelectTrigger className="h-9 w-[160px]" data-testid="advertisers-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {listQuery.isLoading ? (
                    <div className="flex justify-center py-16"><Spinner size="lg" /></div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        icon={<Building2 className="h-8 w-8" />}
                        title={search || statusFilter !== "all" ? "No advertisers match those filters" : "No advertisers yet"}
                        description={search || statusFilter !== "all" ? "Try a different filter." : "Create one to start attributing pages, drafts, banners and domains."}
                        action={!search && statusFilter === "all" ? (
                            <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> New advertiser</Button>
                        ) : undefined}
                    />
                ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm" data-testid="advertisers-table">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Slug</th>
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <th className="px-3 py-2 text-right">Domains</th>
                                    <th className="px-3 py-2 text-right">Pages</th>
                                    <th className="px-3 py-2 text-right">Drafts</th>
                                    <th className="px-3 py-2 text-right">Banners</th>
                                    <th className="px-3 py-2 text-right">LLM&nbsp;$</th>
                                    <th className="px-3 py-2 text-right">Domain&nbsp;$</th>
                                    <th className="px-3 py-2 text-right">Image&nbsp;$</th>
                                    <th className="px-3 py-2 text-right">Total&nbsp;$</th>
                                    <th className="px-3 py-2 text-left">Updated</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((a) => {
                                    const d = dashboardById.get(a.id);
                                    const variant = STATUS_VARIANT[a.status] ?? "default";
                                    return (
                                        <tr
                                            key={a.id}
                                            onClick={() => navigate(`/advertisers/${a.id}`)}
                                            className={cn("cursor-pointer hover:bg-muted/30")}
                                            data-testid={`advertiser-row-${a.id}`}
                                        >
                                            <td className="px-3 py-2">
                                                <Badge variant="outline" className="font-mono text-[11px]">{a.slug}</Badge>
                                            </td>
                                            <td className="px-3 py-2 font-medium">{a.name}</td>
                                            <td className="px-3 py-2">
                                                <Badge variant={variant}>{a.status}</Badge>
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">{d?.domainsOwned ?? "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{d?.pagesOwned ?? "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{d?.draftsOwned ?? "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{d?.bannersOwned ?? "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{microsToUsd(d?.llmCostUsdMicros)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{microsToUsd(d?.domainSpendUsdMicros)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{microsToUsd(d?.imageGenCostUsdMicros)}</td>
                                            <td className="px-3 py-2 text-right font-semibold tabular-nums">{microsToUsd(d?.totalSpendUsdMicros)}</td>
                                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(a.updatedAt)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <CreateAdvertiserDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={(adv) => {
                    queryClient.invalidateQueries({ queryKey: ["advertisers", "list"] });
                    navigate(`/advertisers/${adv.id}`);
                }}
            />
        </>
    );
}

function microsToUsd(value: number | undefined): string {
    if (value == null) return "—";
    if (value === 0) return "$0.00";
    return `$${(value / 1_000_000).toFixed(2)}`;
}

function CreateAdvertiserDialog({
    open,
    onOpenChange,
    onCreated,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated: (adv: AdvertiserDto) => void;
}): React.ReactNode {
    const [slug, setSlug] = React.useState("");
    const [name, setName] = React.useState("");
    const [contactEmail, setContactEmail] = React.useState("");
    const [billingCurrency, setBillingCurrency] = React.useState("USD");
    const [notes, setNotes] = React.useState("");

    React.useEffect(() => {
        if (open) {
            setSlug(""); setName(""); setContactEmail(""); setBillingCurrency("USD"); setNotes("");
        }
    }, [open]);

    const createMut = useMutation({
        mutationFn: (req: CreateAdvertiserRequest) => createAdvertiser(req),
        onSuccess: (adv) => {
            toast.success(`Advertiser "${adv.name}" created`);
            onOpenChange(false);
            onCreated(adv);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't create advertiser")),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="advertiser-create-dialog">
                <DialogHeader>
                    <DialogTitle>New advertiser</DialogTitle>
                    <DialogDescription>
                        The slug is the stable identifier used by attribution; the name shows up in dashboards and the Edit popover.
                    </DialogDescription>
                </DialogHeader>
                <form
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        createMut.mutate({
                            slug: slug.trim(),
                            name: name.trim(),
                            contactEmail: contactEmail.trim() || null,
                            billingCurrency: billingCurrency.trim() || null,
                            notes: notes.trim() || null,
                        });
                    }}
                >
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="adv-slug">Slug</Label>
                            <Input
                                id="adv-slug"
                                data-testid="advertiser-slug-input"
                                value={slug}
                                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                                placeholder="acme-co"
                                required
                                autoFocus
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="adv-name">Name</Label>
                            <Input
                                id="adv-name"
                                data-testid="advertiser-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Acme Co."
                                required
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="adv-email">Contact email</Label>
                            <Input
                                id="adv-email"
                                type="email"
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                                placeholder="ops@acme.example"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="adv-currency">Billing currency</Label>
                            <Input
                                id="adv-currency"
                                value={billingCurrency}
                                onChange={(e) => setBillingCurrency(e.target.value.toUpperCase().slice(0, 3))}
                                placeholder="USD"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="adv-notes">Notes</Label>
                        <Textarea
                            id="adv-notes"
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Internal notes — billing cadence, account manager, etc."
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={createMut.isPending}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="advertiser-create-submit"
                            disabled={!slug.trim() || !name.trim() || createMut.isPending}
                        >
                            {createMut.isPending ? <Spinner size="sm" /> : "Create advertiser"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
